import { useState, useEffect } from 'react'
import { RiArrowRightLine, RiCloseLine, RiHeartFill } from 'react-icons/ri'
import { subscribeToMailchimp } from '@/utils/mailchimp'

const STORAGE_KEY = 'her_newsletter'
const DISMISS_DAYS = 30
const SHOW_DELAY_MS = 5000

const perks = [
  'Birth support resources & tips',
  'New service announcements',
  'Exclusive community offers',
]

function shouldShow(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return true
    const { status, ts } = JSON.parse(raw) as { status: string; ts: number }
    if (status === 'subscribed') return false
    if (status === 'dismissed') {
      const daysSince = (Date.now() - ts) / 86_400_000
      return daysSince >= DISMISS_DAYS
    }
    return true
  } catch {
    return true
  }
}

function markDismissed() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: 'dismissed', ts: Date.now() }))
}

function markSubscribed() {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ status: 'subscribed', ts: Date.now() }))
}

export const NewsletterPopup = () => {
  const [visible, setVisible] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!shouldShow()) return
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS)
    return () => clearTimeout(timer)
  }, [])

  const dismiss = () => {
    setVisible(false)
    markDismissed()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await subscribeToMailchimp(email, name)
      if (res.result === 'success') {
        markSubscribed()
        setSubmitted(true)
        setTimeout(() => setVisible(false), 2800)
      } else {
        const clean = res.msg.replace(/^[0-9]+ - /, '').replace(/<[^>]+>/g, '')
        setError(clean || 'Something went wrong. Please try again.')
      }
    } catch {
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (!visible) return null

  return (
    <>
      <div
        aria-hidden="true"
        onClick={dismiss}
        className="fixed inset-0 z-[80] bg-black/45 backdrop-blur-sm animate-fade-in"
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label="Join the H.E.R newsletter"
        className="fixed inset-0 z-[81] flex items-center justify-center p-4"
      >
        <div className="relative bg-white rounded-3xl shadow-elevated max-w-[440px] w-full overflow-hidden animate-fade-in-up">
          <div className="h-1.5 bg-gradient-to-r from-crimson-600 via-gold-500 to-crimson-600" />

          <button
            onClick={dismiss}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-base-100 flex items-center justify-center text-base-500 hover:text-base-900 hover:bg-base-200 transition-colors"
            aria-label="Close newsletter popup"
          >
            <RiCloseLine size={17} />
          </button>

          <div className="px-8 py-7 space-y-5">
            <div className="w-11 h-11 rounded-full bg-crimson-50 flex items-center justify-center">
              <RiHeartFill size={20} className="text-crimson-600" />
            </div>

            <div className="space-y-1.5">
              <p className="text-crimson-600 text-[10px] font-bold uppercase tracking-[0.26em]">
                Stay Connected
              </p>
              <h2 className="font-display font-bold text-base-900 text-[1.55rem] leading-tight">
                Join the <em className="text-crimson-600 not-italic italic">H.E.R Community</em>
              </h2>
              <p className="text-base-600 text-[13.5px] leading-relaxed">
                Resources, birth support tips, and updates — delivered gently to your inbox.
              </p>
            </div>

            <ul className="space-y-2">
              {perks.map((p) => (
                <li key={p} className="flex items-center gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-gold-500 shrink-0" />
                  <span className="text-[13px] text-base-600">{p}</span>
                </li>
              ))}
            </ul>

            {submitted ? (
              <div className="text-center py-3 space-y-1">
                <p className="font-display font-bold text-base-900 text-lg">You're in!</p>
                <p className="text-[13px] text-base-500">
                  Welcome to the H.E.R community. Check your inbox soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (Optional)"
                  disabled={loading}
                  className="w-full border border-base-200 rounded-full px-5 py-3 text-[14px] text-base-900 placeholder:text-base-400 focus:outline-none focus:ring-2 focus:ring-crimson-600/25 transition disabled:opacity-60"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  disabled={loading}
                  className="w-full border border-base-200 rounded-full px-5 py-3 text-[14px] text-base-900 placeholder:text-base-400 focus:outline-none focus:ring-2 focus:ring-crimson-600/25 transition disabled:opacity-60"
                />
                {error && <p className="text-[12px] text-danger-600 pl-2">{error}</p>}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full inline-flex items-center justify-center gap-2 bg-crimson-600 hover:bg-crimson-700 disabled:opacity-70 text-white font-semibold text-[14px] px-6 py-3 rounded-full transition-colors duration-200"
                >
                  {loading ? 'Joining…' : 'Join the Community'}
                  {!loading && <RiArrowRightLine size={16} />}
                </button>
              </form>
            )}

            <p className="text-[11px] text-base-400 text-center">No spam. Unsubscribe any time.</p>
          </div>
        </div>
      </div>
    </>
  )
}
